import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  IoPersonAddOutline,
  IoCheckmarkOutline,
  IoCloseOutline,
  IoTimeOutline,
  IoChatbubbleEllipsesOutline,
  IoEllipsisHorizontal
} from 'react-icons/io5';
import Avatar from './Avatar';
import './UserRelationshipCard.css';

// Simple Dropdown Component
const ActionDropdown = ({ onUnfriend, onBlock }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="action-dropdown" ref={dropdownRef}>
      <button
        className="dropdown-trigger"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        aria-label="More actions"
      >
        <IoEllipsisHorizontal />
      </button>
      {isOpen && (
        <div className="dropdown-menu">
          <button
            className="dropdown-item danger"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsOpen(false);
              onUnfriend?.();
            }}
          >
            <IoCloseOutline />
            <span>{t('friends.unfriend') || 'Hủy kết bạn'}</span>
          </button>
          <button
            className="dropdown-item"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsOpen(false);
              onBlock?.();
            }}
          >
            <span>{t('friends.block') || 'Chặn'}</span>
          </button>
        </div>
      )}
    </div>
  );
};

const UserRelationshipCard = ({
  user,
  relationship = 'none',
  onAddFriend,
  onAccept,
  onReject,
  onCancelRequest,
  onUnfriend,
  onMessage,
  loading = false,
  showLink = true
}) => {
  const { t } = useTranslation();
  const [actionLoading, setActionLoading] = useState(null);

  const handleAction = async (actionFn, actionName) => {
    setActionLoading(actionName);
    try {
      await actionFn();
    } catch (error) {
      console.error(`Failed to ${actionName}:`, error);
    } finally {
      setActionLoading(null);
    }
  };

  const renderButton = () => {
    if (relationship === 'friends') {
      return (
        <>
          <button
            className="action-btn primary-small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAction(() => onMessage?.(user), 'message');
            }}
            disabled={actionLoading === 'message'}
          >
            {actionLoading === 'message' ? (
              <span className="spinner"></span>
            ) : (
              <>
                <IoChatbubbleEllipsesOutline />
                <span>{t('chat.message') || 'Message'}</span>
              </>
            )}
          </button>
          <ActionDropdown
            onUnfriend={() => handleAction(() => onUnfriend?.(user), 'unfriend')}
          />
        </>
      );
    }

    if (relationship === 'pending_received') {
      return (
        <>
          <button
            className="action-btn primary-small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAction(() => onAccept?.(user), 'accept');
            }}
            disabled={actionLoading === 'accept'}
          >
            {actionLoading === 'accept' ? (
              <span className="spinner"></span>
            ) : (
              <>
                <IoCheckmarkOutline />
                <span>{t('friends.accept') || 'Accept'}</span>
              </>
            )}
          </button>
          <button
            className="action-btn outline"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAction(() => onReject?.(user), 'reject');
            }}
            disabled={actionLoading === 'reject'}
          >
            {actionLoading === 'reject' ? (
              <span className="spinner"></span>
            ) : (
              <IoCloseOutline />
            )}
          </button>
        </>
      );
    }

    if (relationship === 'pending_sent') {
      return (
        <button
          className="action-btn outline"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleAction(() => onCancelRequest?.(user), 'cancel');
          }}
          disabled={actionLoading === 'cancel'}
        >
          {actionLoading === 'cancel' ? (
            <span className="spinner"></span>
          ) : (
            <>
              <IoTimeOutline />
              <span>{t('friends.cancelRequest') || 'Cancel'}</span>
            </>
          )}
        </button>
      );
    }

    // relationship === 'none' (not friends)
    return (
      <button
        className="action-btn primary-small"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleAction(() => onAddFriend?.(user), 'add');
        }}
        disabled={actionLoading === 'add'}
      >
        {actionLoading === 'add' ? (
          <span className="spinner"></span>
        ) : (
          <>
            <IoPersonAddOutline />
            <span>{t('friends.addFriend') || 'Add Friend'}</span>
          </>
        )}
      </button>
    );
  };

  const cardContent = (
    <>
      <Avatar user={user} size={48} />
      <div className="user-info">
        <p className="user-name">{user.display_name}</p>
        <p className="user-username">@{user.username}</p>
      </div>
      <div className="user-actions">
        {renderButton()}
      </div>
    </>
  );

  if (showLink) {
    return (
      <Link
        to={`/profile/${user.username}`}
        className={`user-relationship-card ${loading ? 'loading' : ''}`}
      >
        {cardContent}
      </Link>
    );
  }

  return (
    <div className={`user-relationship-card ${loading ? 'loading' : ''}`}>
      {cardContent}
    </div>
  );
};

// Skeleton loading component
export const UserRelationshipCardSkeleton = () => (
  <div className="user-relationship-card skeleton">
    <div className="skeleton-avatar"></div>
    <div className="skeleton-info">
      <div className="skeleton-name"></div>
      <div className="skeleton-username"></div>
    </div>
    <div className="skeleton-actions">
      <div className="skeleton-button"></div>
    </div>
  </div>
);

export default UserRelationshipCard;
